/**
 * How a stored source reads to a person — one place for every Site Usage table
 * and filter, so the same arrival is never named two ways.
 *
 * Display only, and shown as "Mapped name (actual value)" — `Instagram (ig)` —
 * so the name reads at a glance and the stored value is still there to put in
 * a segment or a query. The stored values stay exactly what arrived (`ig` is
 * Meta's own `{{site_source_name}}` in the ad URL); a value with no better name
 * is shown once.
 */

const both = (label: string | undefined, raw: string): string =>
  (!label || label === raw ? raw : `${label} (${raw})`);

/** Meta's `{{site_source_name}}` and the other short codes a URL tag carries. */
const SOURCE_CODES: Record<string, string> = {
  ig: 'Instagram',
  fb: 'Facebook',
  msg: 'Messenger',
  an: 'Meta Audience Network',
  wa: 'WhatsApp',
  yt: 'YouTube',
};

/** How an account was made when nobody arrived — `provisioned:<how>`. */
const PROVISIONED: Record<string, string> = {
  'teacher-invite': 'Invited by a teacher',
  'admin-create': 'Created by an admin',
  'admin-invite': 'Invited by an admin',
  seeded: 'Seeded account',
};

/** Referring hosts and app ids that have a better name than themselves. */
function hostLabel(host: string): string | undefined {
  const h = host.toLowerCase().replace(/^www\./, '');
  if (h === 'com.google.android.googlequicksearchbox' || h === 'com.google.android.gm') return 'Google app';
  if (/(^|\.)google\.[a-z.]+$/.test(h)) return 'Google';
  if (/(^|\.)instagram\.com$/.test(h)) return 'Instagram';
  if (/(^|\.)(facebook\.com|fb\.com|fb\.me)$/.test(h)) return 'Facebook';
  if (/(^|\.)(youtube\.com|youtu\.be)$/.test(h)) return 'YouTube';
  if (/(^|\.)(whatsapp\.com|wa\.me)$/.test(h)) return 'WhatsApp';
  if (h === 'com.linkedin.android' || /(^|\.)linkedin\.com$/.test(h)) return 'LinkedIn';
  if (/(^|\.)bing\.com$/.test(h)) return 'Bing';
  if (/(^|\.)duckduckgo\.com$/.test(h)) return 'DuckDuckGo';
  if (/(^|\.)(chatgpt\.com|openai\.com)$/.test(h)) return 'ChatGPT';
  return undefined;
}

/** A name and, when the name is not the raw value, the raw value it stands for. */
const parts = (label: string | undefined, raw: string): { label: string; detail?: string } =>
  (!label || label === raw ? { label: raw } : { label, detail: raw });

/**
 * A first-touch source split for a table cell: the readable name, and the raw
 * value behind it (`com.google.android.googlequicksearchbox`,
 * `provisioned:teacher-invite`) for an info tip rather than the cell itself.
 */
export function firstTouchParts(source: string): { label: string; detail?: string } {
  if (SOURCE_CODES[source]) return parts(SOURCE_CODES[source], source);
  if (source === 'direct') return { label: 'Direct', detail: 'Nothing but the page itself — no referrer, no tags' };
  if (source === '(not recorded)') return { label: 'Not recorded', detail: 'An account from before first touches were recorded' };
  if (source === '(no account)') return { label: 'No account' };
  if (source.startsWith('link:')) return parts(`Link /p/${source.slice(5)}`, source);
  if (source.startsWith('provisioned:')) return parts(PROVISIONED[source.slice(12)] ?? 'Provisioned', source);
  return parts(hostLabel(source), source);
}

/**
 * A first-touch source — backend `sourceOf`: a URL tag (`ig`), a published link
 * (`link:<slug>`), a referring host, how it was provisioned, `direct`, or
 * `(not recorded)`.
 */
export function firstTouchLabel(source: string): string {
  if (SOURCE_CODES[source]) return both(SOURCE_CODES[source], source);
  if (source === 'direct') return 'Direct';
  if (source === '(not recorded)') return 'Not recorded';
  if (source === '(no account)') return 'No account';
  if (source.startsWith('link:')) return both(`Link /p/${source.slice(5)}`, source);
  if (source.startsWith('provisioned:')) return both(PROVISIONED[source.slice(12)] ?? 'Provisioned', source);
  return both(hostLabel(source), source);
}

/** A referrer kind — backend `classifySource`: `google`, `our-sites`, `none`,
 *  `other:<host>`… */
export function referrerKindLabel(kind: string): string {
  if (kind === 'none') return 'No referrer';
  if (kind === 'our-sites') return 'Our sites';
  if (kind === 'search') return 'Other search engine';
  if (kind.startsWith('other:')) return both(hostLabel(kind.slice(6)), kind.slice(6));
  return kind.charAt(0).toUpperCase() + kind.slice(1);
}

/** The stored property (`zitmus`, `student`) or report site (`science`, `app`). */
const SITE: Record<string, string> = {
  zitmus: 'Science site', science: 'Science site', student: 'BuzzBrain app', app: 'BuzzBrain app',
};
export const siteLabel = (v: string): string => SITE[v] ?? v;
